import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';

import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  applyUpdate,
  createEndpoint,
  deleteEndpoint,
  errorText,
  fetchEndpoint,
  fetchEndpoints,
  postAction,
  type ActionOutcome,
  type Endpoint,
  type EndpointUpdate,
  type GroupState,
  type InventoryItem,
  type ItemState,
  type NewEndpoint,
} from './controlApi';
import {
  fetchAppliances,
  fetchSites,
  LiveClient,
  liveUrl,
  type Appliance,
  type LiveState,
  type Site,
} from './fleetApi';
import { ConfirmDialog } from './shared/ConfirmDialog';
import { FormDialog } from './shared/FormDialog';
import { PageHeader } from './shared/PageHeader';
import { Throttle } from './throttle';

interface SiteControlProps {
  apiUrl: string;
  tenantId: string;
  siteId: string;
  token: string;
  permissions: string[];
  /** Tests inject a fake WebSocket. */
  socketFactory?: (url: string) => WebSocket;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; site: Site; appliances: Appliance[]; endpoints: Endpoint[] }
  | { kind: 'missing' }
  | { kind: 'error'; error: string };

type Run = (
  endpointId: string,
  action: string,
  params: Record<string, unknown>,
) => Promise<ActionOutcome>;

/**
 * A site's lighting endpoints with live state and controls (UI-002): presets
 * and scenes toggle, groups take intensity and colour. Controls are disabled
 * while the site's appliance or the processor is offline (UI-011).
 */
export function SiteControl({
  apiUrl,
  tenantId,
  siteId,
  token,
  permissions,
  socketFactory,
}: SiteControlProps) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [live, setLive] = useState<LiveState>('connecting');
  const [toast, setToast] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const tokenRef = useRef(token);
  const clientRef = useRef<LiveClient | undefined>(undefined);
  const canControl = permissions.includes('action.execute');
  const canConfigure = permissions.includes('endpoint.write');

  const reload = useCallback(
    (signal?: AbortSignal) => {
      const t = tokenRef.current;
      Promise.all([
        fetchSites(apiUrl, t, tenantId, signal),
        fetchAppliances(apiUrl, t, tenantId, signal),
        fetchEndpoints(apiUrl, t, tenantId, siteId, signal),
      ])
        .then(([sites, appliances, endpoints]) => {
          const site = sites.find((s) => s.id === siteId);
          if (!site) {
            setLoad({ kind: 'missing' });
            return;
          }
          setLoad({
            kind: 'ready',
            site,
            appliances: appliances.filter((a) => a.site_id === siteId),
            endpoints,
          });
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          setLoad({ kind: 'error', error: errorText(err) });
        });
    },
    [apiUrl, tenantId, siteId],
  );

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => {
      controller.abort();
    };
  }, [reload]);

  const refreshEndpoint = useCallback(
    (id: string) => {
      fetchEndpoint(apiUrl, tokenRef.current, tenantId, siteId, id)
        .then((ep) => {
          setLoad((prev) =>
            prev.kind === 'ready'
              ? {
                  ...prev,
                  endpoints: prev.endpoints.some((e) => e.id === id)
                    ? prev.endpoints.map((e) => (e.id === id ? ep : e))
                    : [...prev.endpoints, ep],
                }
              : prev,
          );
        })
        .catch(() => {
          reload();
        });
    },
    [apiUrl, tenantId, siteId, reload],
  );

  // Live presence and endpoint state for this site.
  useEffect(() => {
    const onEndpoint = (u: EndpointUpdate) => {
      if (u.site_id !== siteId) return;
      if (u.kind === 'deleted') {
        setLoad((prev) =>
          prev.kind === 'ready'
            ? { ...prev, endpoints: prev.endpoints.filter((e) => e.id !== u.id) }
            : prev,
        );
        return;
      }
      const outcome = { stale: false };
      setLoad((prev) => {
        if (prev.kind !== 'ready') return prev;
        const current = prev.endpoints.find((e) => e.id === u.id);
        const next = current ? applyUpdate(current, u) : null;
        if (!next) {
          outcome.stale = true;
          return prev;
        }
        return { ...prev, endpoints: prev.endpoints.map((e) => (e.id === u.id ? next : e)) };
      });
      // Too large to carry, or an endpoint we don't have yet: reload it.
      if (outcome.stale) refreshEndpoint(u.id);
    };
    const client = new LiveClient({
      url: liveUrl(apiUrl),
      tenantId,
      siteId,
      getToken: () => tokenRef.current,
      socketFactory,
      onState: setLive,
      onResync: () => {
        reload();
      },
      onAppliance: (a) => {
        setLoad((prev) =>
          prev.kind === 'ready' && a.site_id === siteId
            ? {
                ...prev,
                appliances: prev.appliances.map((x) => (x.id === a.id ? { ...x, ...a } : x)),
              }
            : prev,
        );
      },
      onEndpoint,
    });
    clientRef.current = client;
    return () => {
      client.close();
    };
  }, [apiUrl, tenantId, siteId, reload, refreshEndpoint, socketFactory]);

  useEffect(() => {
    tokenRef.current = token;
    clientRef.current?.refreshToken();
  }, [token]);

  // Actions travel on the live socket when it's up, else over REST.
  const run: Run = useCallback(
    (endpointId, action, params) => {
      const req = { endpoint_id: endpointId, action, params };
      const viaSocket = clientRef.current?.action({ ...req, site_id: siteId });
      return viaSocket ?? postAction(apiUrl, tokenRef.current, tenantId, siteId, req);
    },
    [apiUrl, tenantId, siteId],
  );
  const runReporting: Run = useCallback(
    async (endpointId, action, params) => {
      try {
        return await run(endpointId, action, params);
      } catch (err) {
        setToast(errorText(err));
        throw err;
      }
    },
    [run],
  );

  if (load.kind === 'loading') {
    return <Typography color="text.secondary">Loading site…</Typography>;
  }
  if (load.kind === 'missing') {
    return (
      <Stack spacing={2}>
        <PageHeader title="Site not found" crumbs={[{ label: 'Sites', path: '/' }]} />
        <Alert severity="warning">This site doesn&apos;t exist or isn&apos;t in your access.</Alert>
      </Stack>
    );
  }
  if (load.kind === 'error') {
    return <Alert severity="error">Could not load the site: {load.error}</Alert>;
  }

  const { site, appliances, endpoints } = load;
  const onlineAppliance = appliances.some((a) => a.status === 'online');

  return (
    <Stack spacing={2} data-testid="site-control">
      <PageHeader
        title={site.name}
        subtitle={site.timezone}
        crumbs={[{ label: 'Sites', path: '/' }]}
        actions={
          <>
            <Chip
              size="small"
              data-testid="live-state"
              color={live === 'live' ? 'success' : 'default'}
              variant={live === 'live' ? 'filled' : 'outlined'}
              label={
                live === 'live' ? 'Live' : live === 'connecting' ? 'Connecting…' : 'Reconnecting…'
              }
            />
            {canConfigure && appliances.length > 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                data-testid="add-endpoint-open"
                onClick={() => {
                  setAdding(true);
                }}
              >
                Add endpoint
              </Button>
            )}
          </>
        }
      />

      {appliances.length === 0 && (
        <Alert severity="info">
          No appliance is installed at this site yet, so there is nothing to control.
        </Alert>
      )}
      {appliances.length > 0 && !onlineAppliance && (
        <Alert severity="warning" data-testid="site-offline">
          This site&apos;s appliance is offline. Live control is unavailable until it reconnects;
          the state shown is the last known.
        </Alert>
      )}
      {!canControl && (
        <Alert severity="info" data-testid="view-only">
          You can view this site but not control it.
        </Alert>
      )}
      {appliances.length > 0 && endpoints.length === 0 && (
        <Alert severity="info">
          No lighting endpoints yet.
          {canConfigure ? ' Add the site’s lighting processor to control it here.' : ''}
        </Alert>
      )}

      {endpoints.map((ep) => (
        <EndpointCard
          key={ep.id}
          endpoint={ep}
          appliance={appliances.find((a) => a.id === ep.appliance_id)}
          canControl={canControl}
          run={runReporting}
          onDelete={
            canConfigure
              ? async () => {
                  await deleteEndpoint(apiUrl, tokenRef.current, tenantId, siteId, ep.id);
                  setLoad((prev) =>
                    prev.kind === 'ready'
                      ? { ...prev, endpoints: prev.endpoints.filter((e) => e.id !== ep.id) }
                      : prev,
                  );
                }
              : undefined
          }
        />
      ))}

      {canConfigure && (
        <AddEndpointDialog
          open={adding}
          appliances={appliances}
          onClose={() => {
            setAdding(false);
          }}
          onCreate={async (ep) => {
            const created = await createEndpoint(apiUrl, tokenRef.current, tenantId, siteId, ep);
            setLoad((prev) =>
              prev.kind === 'ready' && !prev.endpoints.some((e) => e.id === created.id)
                ? { ...prev, endpoints: [...prev.endpoints, created] }
                : prev,
            );
          }}
        />
      )}

      <Snackbar
        open={toast !== null}
        autoHideDuration={6000}
        onClose={() => {
          setToast(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          data-testid="action-error"
          onClose={() => {
            setToast(null);
          }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

// ---------------------------------------------------------------- endpoint card

function supports(ep: Endpoint, action: string): boolean {
  return ep.state?.capabilities.actions?.some((a) => a.name === action) ?? false;
}

function EndpointCard({
  endpoint: ep,
  appliance,
  canControl,
  run,
  onDelete,
}: {
  endpoint: Endpoint;
  appliance: Appliance | undefined;
  canControl: boolean;
  run: Run;
  onDelete?: () => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const st = ep.state;
  const applianceOnline = appliance?.status === 'online';
  const live = applianceOnline && (st?.online ?? false);
  const disabled = !live || !canControl;

  let status: { label: string; color: 'success' | 'warning' | 'default' };
  if (!applianceOnline) status = { label: 'Appliance offline', color: 'default' };
  else if (!st) status = { label: 'Connecting…', color: 'default' };
  else if (!st.online) status = { label: 'Processor offline', color: 'warning' };
  else status = { label: 'Online', color: 'success' };

  const firmware = typeof st?.driver.firmware === 'string' ? st.driver.firmware : undefined;
  const hardware = typeof st?.driver.hardware === 'string' ? st.driver.hardware : undefined;

  return (
    <Card variant="outlined" data-testid={`endpoint-${ep.name}`}>
      <CardContent>
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              component="h2"
              sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
            >
              {ep.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {ep.driver === 'pharos' ? 'Pharos' : ep.driver}
              {hardware ? ` ${hardware}` : ''}
              {firmware ? ` · firmware ${firmware}` : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
            <Chip
              size="small"
              data-testid="endpoint-status"
              color={status.color}
              label={status.label}
            />
            {onDelete && (
              <IconButton
                size="small"
                aria-label={`Remove ${ep.name}`}
                title={`Remove ${ep.name}`}
                onClick={() => {
                  setConfirming(true);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>

        {applianceOnline && st && !st.online && st.error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {st.error}
          </Alert>
        )}
        {!st && (
          <Typography variant="body2" color="text.secondary">
            Waiting for the appliance to report this endpoint.
          </Typography>
        )}

        {st && (
          <Stack spacing={2}>
            {st.inventory.presets.length > 0 && supports(ep, 'preset.activate') && (
              <ToggleGrid
                title="Presets"
                kind="preset"
                items={st.inventory.presets}
                states={st.state.presets ?? {}}
                disabled={disabled}
                canRelease={supports(ep, 'preset.release')}
                onToggle={(id, active) =>
                  run(ep.id, active ? 'preset.release' : 'preset.activate', { preset: id })
                }
              />
            )}
            {st.inventory.scenes.length > 0 && supports(ep, 'scene.activate') && (
              <ToggleGrid
                title="Scenes"
                kind="scene"
                items={st.inventory.scenes}
                states={st.state.scenes ?? {}}
                disabled={disabled}
                canRelease={supports(ep, 'scene.release')}
                onToggle={(id, active) =>
                  run(ep.id, active ? 'scene.release' : 'scene.activate', { scene: id })
                }
              />
            )}
            {st.inventory.groups.length > 0 && supports(ep, 'group.set') && (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Groups
                </Typography>
                <Stack spacing={1}>
                  {st.inventory.groups.map((g) => (
                    <GroupRow
                      key={g.id}
                      item={g}
                      state={st.state.groups?.[g.id] ?? {}}
                      disabled={disabled}
                      send={(params) => run(ep.id, 'group.set', { group: g.id, ...params })}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </CardContent>

      {onDelete && (
        <ConfirmDialog
          open={confirming}
          title={`Remove ${ep.name}?`}
          confirmLabel="Remove"
          confirmTestId="confirm"
          onClose={() => {
            setConfirming(false);
          }}
          onConfirm={onDelete}
        >
          The appliance stops controlling this processor. Its presets and history in IllumiLink are
          removed.
        </ConfirmDialog>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- presets & scenes

const STATE_LABELS: Record<string, string> = {
  running: 'Running',
  paused: 'Paused',
  holding_at_end: 'Holding',
  started: 'On',
};

function ToggleGrid({
  title,
  kind,
  items,
  states,
  disabled,
  canRelease,
  onToggle,
}: {
  title: string;
  kind: 'preset' | 'scene';
  items: InventoryItem[];
  states: Record<string, ItemState>;
  disabled: boolean;
  canRelease: boolean;
  onToggle: (id: string, active: boolean) => Promise<ActionOutcome>;
}) {
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const toggle = (id: string, active: boolean) => {
    setPending((p) => new Set(p).add(id));
    onToggle(id, active && canRelease)
      .catch(() => undefined) // reported by the caller
      .finally(() => {
        setPending((p) => {
          const next = new Set(p);
          next.delete(id);
          return next;
        });
      });
  };

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(auto-fill, minmax(150px, 1fr))',
          },
        }}
      >
        {items.map((it) => {
          const s = states[it.id];
          const active = s?.active ?? false;
          const busy = pending.has(it.id);
          const label = s?.state ? STATE_LABELS[s.state] : undefined;
          return (
            <Button
              key={it.id}
              variant={active ? 'contained' : 'outlined'}
              color={active ? 'primary' : 'inherit'}
              aria-pressed={active}
              disabled={disabled || busy}
              data-testid={`${kind}-${it.id}`}
              onClick={() => {
                toggle(it.id, active);
              }}
              sx={{
                minHeight: 56,
                justifyContent: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                flexDirection: 'column',
                alignItems: 'flex-start',
                position: 'relative',
              }}
            >
              <Typography
                component="span"
                sx={{ fontWeight: 600, lineHeight: 1.2, overflowWrap: 'anywhere' }}
              >
                {it.name}
              </Typography>
              {active && label && (
                <Typography component="span" variant="caption" sx={{ opacity: 0.85 }}>
                  {label}
                </Typography>
              )}
              {busy && (
                <CircularProgress
                  size={16}
                  aria-label="Sending"
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                />
              )}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- groups

const SEND_EVERY_MS = 150; // continuous controls: about 7 updates/s (ADR-0009)
const HOLD_LOCAL_MS = 1500; // keep the user's value until the processor reports it

/** A throttled sender for one continuous control (see Throttle). */
function useThrottled(send: (params: Record<string, unknown>) => Promise<ActionOutcome>) {
  const [throttle] = useState(
    () =>
      new Throttle<Record<string, unknown>>(SEND_EVERY_MS, (p) => {
        send(p).catch(() => undefined); // reported by the caller
      }),
  );
  useEffect(() => {
    throttle.setSend((p) => {
      send(p).catch(() => undefined);
    });
  }, [throttle, send]);
  useEffect(
    () => () => {
      throttle.cancel();
    },
    [throttle],
  );
  return useCallback(
    (params: Record<string, unknown>, immediate = false) => {
      throttle.push(params, immediate);
    },
    [throttle],
  );
}

function toHex(rgb: readonly number[] | undefined): string {
  if (!rgb || rgb.length !== 3) return '#ffffff';
  return `#${rgb
    .map((c) =>
      Math.max(0, Math.min(255, Math.round(c)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

function fromHex(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function GroupRow({
  item,
  state,
  disabled,
  send,
}: {
  item: InventoryItem;
  state: GroupState;
  disabled: boolean;
  send: (params: Record<string, unknown>) => Promise<ActionOutcome>;
}) {
  const throttled = useThrottled(send);
  const [local, setLocal] = useState<{ intensity?: number; rgb?: string; until: number } | null>(
    null,
  );
  const holdTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(
    () => () => {
      clearTimeout(holdTimer.current);
    },
    [],
  );

  const hold = (patch: { intensity?: number; rgb?: string }) => {
    setLocal((prev) => ({ ...(prev ?? {}), ...patch, until: Date.now() + HOLD_LOCAL_MS }));
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => {
      setLocal(null);
    }, HOLD_LOCAL_MS);
  };

  const intensity = local?.intensity ?? state.intensity ?? 0;
  const colour = local?.rgb ?? toHex(state.rgb);
  const percent = Math.round((intensity / 255) * 100);

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.5, sm: 2 }}
      data-testid={`group-${item.id}`}
      sx={{ alignItems: { sm: 'center' }, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}
    >
      <Typography sx={{ minWidth: { sm: 140 }, fontWeight: 500, overflowWrap: 'anywhere' }}>
        {item.name}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexGrow: 1 }}>
        <Slider
          aria-label={`${item.name} intensity`}
          value={intensity}
          min={0}
          max={255}
          disabled={disabled}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${String(Math.round((v / 255) * 100))}%`}
          data-testid={`group-${item.id}-slider`}
          onChange={(_: Event, v: number | number[]) => {
            const value = Array.isArray(v) ? (v[0] ?? 0) : v;
            hold({ intensity: value });
            throttled({ intensity: value });
          }}
          onChangeCommitted={(_: unknown, v: number | number[]) => {
            const value = Array.isArray(v) ? (v[0] ?? 0) : v;
            hold({ intensity: value });
            throttled({ intensity: value }, true);
          }}
          sx={{ flexGrow: 1 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ width: 40, textAlign: 'right' }}>
          {percent}%
        </Typography>
        <Box
          component="input"
          type="color"
          aria-label={`${item.name} colour`}
          value={colour}
          disabled={disabled}
          data-testid={`group-${item.id}-colour`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            hold({ rgb: e.target.value });
            throttled({ rgb: fromHex(e.target.value) });
          }}
          sx={{
            width: 40,
            height: 32,
            p: 0,
            border: 'none',
            borderRadius: 1,
            bgcolor: 'transparent',
            cursor: disabled ? 'default' : 'pointer',
            flexShrink: 0,
          }}
        />
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------- dialogs

function AddEndpointDialog({
  open,
  appliances,
  onClose,
  onCreate,
}: {
  open: boolean;
  appliances: Appliance[];
  onClose: () => void;
  onCreate: (ep: NewEndpoint) => Promise<void>;
}) {
  const [applianceId, setApplianceId] = useState('');
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [tls, setTls] = useState(false);
  const [insecure, setInsecure] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const appliance = applianceId || appliances[0]?.id || '';
  const portValid =
    port === '' || (/^\d+$/.test(port) && Number(port) >= 1 && Number(port) <= 65535);

  const submit = async () => {
    const settings: Record<string, unknown> = { host: host.trim() };
    if (port) settings.port = Number(port);
    if (tls) {
      settings.tls = true;
      if (insecure) settings.tls_insecure_skip_verify = true;
    }
    const ep: NewEndpoint = {
      appliance_id: appliance,
      name: name.trim(),
      driver: 'pharos',
      settings,
    };
    if (username || password) ep.credentials = { username, password };
    await onCreate(ep);
    setName('');
    setHost('');
    setPort('');
    setUsername('');
    setPassword('');
    onClose();
  };

  return (
    <FormDialog
      open={open}
      title="Add endpoint"
      submitLabel="Add"
      canSubmit={Boolean(name.trim() && host.trim() && appliance && portValid)}
      submitTestId="ep-submit"
      onClose={onClose}
      onSubmit={submit}
    >
      <Typography variant="body2" color="text.secondary">
        A Pharos controller on the site&apos;s lighting network, reachable from the appliance.
      </Typography>
      {appliances.length > 1 && (
        <FormControl>
          <InputLabel id="ep-appliance-label">Appliance</InputLabel>
          <Select
            labelId="ep-appliance-label"
            label="Appliance"
            value={appliance}
            onChange={(e) => {
              setApplianceId(e.target.value);
            }}
          >
            {appliances.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.name ?? a.serial}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <TextField
        label="Name"
        value={name}
        placeholder="e.g. Lobby LPC"
        slotProps={{ htmlInput: { 'data-testid': 'ep-name' } }}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Controller address"
          value={host}
          placeholder="10.0.0.20"
          sx={{ flexGrow: 1 }}
          slotProps={{
            htmlInput: { 'data-testid': 'ep-host', autoCapitalize: 'off', spellCheck: false },
          }}
          onChange={(e) => {
            setHost(e.target.value);
          }}
        />
        <TextField
          label="Port"
          value={port}
          placeholder={tls ? '443' : '80'}
          error={!portValid}
          sx={{ width: { sm: 120 } }}
          slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'ep-port' } }}
          onChange={(e) => {
            setPort(e.target.value.trim());
          }}
        />
      </Stack>
      <FormControlLabel
        control={
          <Switch
            checked={tls}
            onChange={(e) => {
              setTls(e.target.checked);
            }}
          />
        }
        label="Use HTTPS"
      />
      {tls && (
        <FormControlLabel
          control={
            <Switch
              checked={insecure}
              onChange={(e) => {
                setInsecure(e.target.checked);
              }}
            />
          }
          label="Accept the controller's self-signed certificate"
        />
      )}
      <Typography variant="body2" color="text.secondary">
        Login, if the controller has web security enabled. It&apos;s stored encrypted and never
        shown again.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Username"
          value={username}
          autoComplete="off"
          sx={{ flexGrow: 1 }}
          onChange={(e) => {
            setUsername(e.target.value);
          }}
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          autoComplete="new-password"
          sx={{ flexGrow: 1 }}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
        />
      </Stack>
    </FormDialog>
  );
}
